/*
 * CURAE storefront behaviour.
 *
 * Plain custom elements on top of Horizon. Cart changes go through the Ajax Cart API and
 * are announced with Shopify's standard `CartLinesUpdateEvent`, so Horizon components
 * (quick add, cart icon, etc.) stay in sync, and adds made by Horizon components open the
 * CURAE drawer.
 */
import { formatMoney } from '@theme/money-formatting';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const DRAWER_SECTION = 'curae-cart-drawer';

/** @type {Promise<any> | null} */
let standardEventsPromise = null;
function standardEvents() {
  if (!standardEventsPromise) {
    standardEventsPromise = import('@shopify/events').catch(() => null);
  }
  return standardEventsPromise;
}

function money(cents, format) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return formatMoney(cents, format || window.CuraeTheme?.moneyFormat || '${{amount}}', currency);
  } catch (_) {
    return '$' + (cents / 100).toFixed(2);
  }
}

async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || 'Cart request failed');
    error.data = data;
    throw error;
  }
  return data;
}

async function fetchSection(sectionId, path) {
  const base = path || window.location.pathname;
  const url = `${base}${base.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('Section request failed');
  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * Runs a cart mutation and announces it with Shopify's standard cart event.
 * @param {'add'|'update'|'remove'} action
 * @param {Array<{merchandiseId: string, quantity: number}>} lines
 * @param {() => Promise<void>} mutate
 * @param {EventTarget} target
 */
async function announceCartChange(action, lines, mutate, target) {
  const events = await standardEvents();
  let deferred = null;
  if (events && events.CartLinesUpdateEvent) {
    try {
      deferred = events.CartLinesUpdateEvent.createPromise();
      target.dispatchEvent(
        new events.CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise })
      );
    } catch (_) {
      deferred = null;
    }
  }

  try {
    await mutate();
  } finally {
    const cart = await fetchJSON(`${root}cart.js`).catch(() => null);
    if (cart) CuraeCart.setCount(cart.item_count);
    if (deferred) {
      try {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'curae', items: cart ? cart.items : [] },
        });
      } catch (_) {
        /* ignore */
      }
    }
  }
}

const CuraeCart = {
  setCount(count) {
    document.querySelectorAll('[data-curae-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
  },

  drawer() {
    return document.querySelector('curae-cart-drawer');
  },

  async refreshDrawer() {
    const wrapper = document.getElementById(`shopify-section-${DRAWER_SECTION}`);
    if (!wrapper) return;
    const doc = await fetchSection(DRAWER_SECTION, root);
    const fresh = doc.getElementById(`shopify-section-${DRAWER_SECTION}`);
    if (!fresh) return;
    const wasOpen = this.drawer()?.isOpen;
    wrapper.innerHTML = fresh.innerHTML;
    if (wasOpen) this.drawer()?.open({ focus: false });
  },

  async refreshCartPage() {
    const page = document.querySelector('[data-curae-cart-page]');
    if (!page) return;
    const id = page.dataset.sectionId;
    const doc = await fetchSection(id);
    const fresh = doc.querySelector('[data-curae-cart-page]');
    if (fresh) page.replaceWith(fresh);
  },

  async refreshAll() {
    await Promise.all([this.refreshDrawer(), this.refreshCartPage()]);
  },

  async add(variantId, quantity, target) {
    await announceCartChange(
      'add',
      [{ merchandiseId: String(variantId), quantity }],
      () => fetchJSON(`${root}cart/add.js`, { method: 'POST', body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }) }),
      target || document
    );
    await this.refreshAll();
    const drawer = this.drawer();
    if (drawer) drawer.open();
    else window.location.href = `${root}cart`;
  },

  async change(key, quantity, variantId, target) {
    await announceCartChange(
      quantity === 0 ? 'remove' : 'update',
      [{ merchandiseId: String(variantId || ''), quantity }],
      () => fetchJSON(`${root}cart/change.js`, { method: 'POST', body: JSON.stringify({ id: key, quantity }) }),
      target || document
    );
    await this.refreshAll();
  },
};

window.CuraeCart = CuraeCart;

/* Adds made by Horizon components (quick add, etc.) refresh and open the CURAE drawer. */
standardEvents().then((events) => {
  if (!events || !events.StandardEvents) return;
  document.addEventListener(events.StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    promise
      .then((result) => {
        if (result && result.detail && result.detail.source === 'curae') return;
        if (result && result.cart && typeof result.cart.totalQuantity === 'number') {
          CuraeCart.setCount(result.cart.totalQuantity);
        }
        CuraeCart.refreshAll().then(() => {
          if (/** @type {any} */ (event).action === 'add' && !(result && result.detail && result.detail.didError)) {
            CuraeCart.drawer()?.open();
          }
        });
      })
      .catch(() => {});
  });
});

/* ---------- Quantity stepper ---------- */
class CuraeQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.minus = this.querySelector('[data-step="-1"]');
    this.plus = this.querySelector('[data-step="1"]');
    this.addEventListener('click', this.onClick);
    this.input?.addEventListener('change', this.onInput);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get min() {
    return Number(this.input?.min || 1);
  }

  get max() {
    return Number(this.input?.max || 9);
  }

  get value() {
    return Number(this.input?.value || this.min);
  }

  set value(v) {
    if (!this.input) return;
    const next = Math.min(this.max, Math.max(this.min, Number(v) || this.min));
    this.input.value = String(next);
    this.sync();
  }

  sync() {
    if (this.minus) this.minus.disabled = this.value <= this.min;
    if (this.plus) this.plus.disabled = this.value >= this.max;
  }

  onClick = (event) => {
    const button = event.target.closest('[data-step]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    const before = this.value;
    this.value = before + Number(button.dataset.step);
    if (this.value !== before) this.emit();
  };

  onInput = () => {
    this.value = this.input.value;
    this.emit();
  };

  emit() {
    this.dispatchEvent(new CustomEvent('curae:quantity', { bubbles: true, detail: { value: this.value } }));
  }
}
customElements.define('curae-quantity', CuraeQuantity);

/* ---------- Product form ---------- */
class CuraeProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.quantity = this.querySelector('curae-quantity');
    this.form?.addEventListener('submit', this.onSubmit);
    this.addEventListener('curae:quantity', this.updateLabels);
    document.addEventListener('curae:add-to-cart', this.onExternalAdd);
    this.updateLabels();
  }

  disconnectedCallback() {
    document.removeEventListener('curae:add-to-cart', this.onExternalAdd);
  }

  get variantId() {
    return this.form?.querySelector('[name="id"]')?.value || '';
  }

  get qty() {
    return this.quantity ? this.quantity.value : 1;
  }

  updateLabels = () => {
    const unit = Number(this.dataset.unitPrice || 0);
    const prefix = this.dataset.addLabel || 'Add to cart';
    const label = `${prefix} — ${money(unit * this.qty, this.dataset.moneyFormat)}`;
    document.querySelectorAll('[data-curae-add-label]').forEach((el) => {
      el.textContent = label;
    });
  };

  onExternalAdd = () => {
    this.submit();
  };

  onSubmit = (event) => {
    event.preventDefault();
    this.submit();
  };

  showError(message) {
    const error = this.querySelector('[data-curae-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  async submit() {
    if (this.busy) return;
    const error = this.querySelector('[data-curae-error]');
    if (error) error.hidden = true;
    if (!this.variantId) {
      this.showError(this.dataset.unavailableMessage || "This product isn't available to buy yet.");
      return;
    }
    this.busy = true;
    const buttons = document.querySelectorAll('[data-curae-add]');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      await CuraeCart.add(this.variantId, this.qty, this);
    } catch (err) {
      this.showError(err.message);
    } finally {
      this.busy = false;
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }
}
customElements.define('curae-product-form', CuraeProductForm);

/* Sticky bar button: submits the main product form with its chosen quantity. */
class CuraeStickyAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-curae-add]')) return;
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('curae:add-to-cart'));
    });
  }
}
customElements.define('curae-sticky-add', CuraeStickyAdd);

/* ---------- Cart drawer ---------- */
class CuraeCartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.curae-drawer');
    this.addEventListener('click', this.onClick);
    this.addEventListener('curae:quantity', this.onQuantity);
    this.addEventListener('keydown', this.onKeydown);
  }

  get isOpen() {
    return this.panel && !this.panel.hidden;
  }

  open({ focus = true } = {}) {
    this.panel = this.querySelector('.curae-drawer');
    if (!this.panel) return;
    if (!this.isOpen) this.lastFocus = document.activeElement;
    this.panel.hidden = false;
    document.querySelectorAll('[data-curae-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
    if (focus) this.querySelector('[data-curae-cart-close]:not(.curae-drawer__scrim)')?.focus({ preventScroll: true });
  }

  close() {
    if (!this.panel) return;
    this.panel.hidden = true;
    document.querySelectorAll('[data-curae-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
  }

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  onClick = (event) => {
    if (event.target.closest('[data-curae-cart-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  onQuantity = async (event) => {
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    this.panel?.setAttribute('aria-busy', 'true');
    try {
      await CuraeCart.change(line.dataset.lineKey, event.detail.value, line.dataset.variantId, this);
    } finally {
      this.querySelector('.curae-drawer')?.removeAttribute('aria-busy');
    }
  };
}
customElements.define('curae-cart-drawer', CuraeCartDrawer);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-curae-cart-open]');
  if (!opener) return;
  const drawer = CuraeCart.drawer();
  if (!drawer) return;
  event.preventDefault();
  drawer.open();
});

/* ---------- Cart page ---------- */
document.addEventListener('curae:quantity', async (event) => {
  const page = event.target.closest('[data-curae-cart-page]');
  const line = event.target.closest('[data-line-key]');
  if (!page || !line) return;
  page.setAttribute('aria-busy', 'true');
  try {
    await CuraeCart.change(line.dataset.lineKey, event.detail.value, line.dataset.variantId, page);
  } finally {
    document.querySelector('[data-curae-cart-page]')?.removeAttribute('aria-busy');
  }
});

document.addEventListener('click', async (event) => {
  const remove = event.target.closest('[data-curae-remove]');
  if (!remove) return;
  const line = remove.closest('[data-line-key]');
  if (!line) return;
  event.preventDefault();
  const scope = remove.closest('[data-curae-cart-page], .curae-drawer');
  scope?.setAttribute('aria-busy', 'true');
  try {
    await CuraeCart.change(line.dataset.lineKey, 0, line.dataset.variantId, remove);
  } finally {
    document.querySelectorAll('[data-curae-cart-page], .curae-drawer').forEach((el) => el.removeAttribute('aria-busy'));
  }
});

/* ---------- Product gallery ---------- */
class CuraeGallery extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('.curae-gallery__frame');
    this.slides = Array.from(this.querySelectorAll('.curae-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.curae-gallery__thumb'));
    this.dots = Array.from(this.querySelectorAll('.curae-gallery__dot'));
    this.index = Math.max(0, this.slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false'));
    this.addEventListener('click', this.onClick);
    this.frame?.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.frame?.addEventListener('touchend', this.onTouchEnd);
    window.addEventListener('keydown', this.onKey);
    this.show(this.index);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this.onKey);
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true'));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    this.dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === this.index));
    const ratio = this.slides[this.index].dataset.ratio;
    if (ratio && this.frame) this.frame.style.setProperty('--frame-ratio', ratio);
    this.slides.forEach((slide, i) => {
      if (i !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  onClick = (event) => {
    const thumb = event.target.closest('.curae-gallery__thumb');
    if (thumb) {
      event.preventDefault();
      this.show(this.thumbs.indexOf(thumb));
      return;
    }
    const arrow = event.target.closest('[data-gallery-step]');
    if (arrow) {
      event.preventDefault();
      this.show(this.index + Number(arrow.dataset.galleryStep));
    }
  };

  onKey = (event) => {
    if (CuraeCart.drawer()?.isOpen) return;
    const t = event.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (t && t.closest && t.closest('[role="slider"]')) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.show(this.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.show(this.index - 1);
    }
  };

  onTouchStart = (event) => {
    this.startX = event.touches[0].clientX;
  };

  onTouchEnd = (event) => {
    const dx = event.changedTouches[0].clientX - (this.startX ?? 0);
    if (Math.abs(dx) > 40) this.show(this.index + (dx < 0 ? 1 : -1));
  };
}
customElements.define('curae-gallery', CuraeGallery);

/* ---------- Before / after slider ---------- */
class CuraeBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('.curae-ba__stage');
    this.control = this.querySelector('[role="slider"]');
    if (!this.stage || !this.control) return;
    this.set(Number(this.dataset.start || 50));
    this.control.addEventListener('pointerdown', this.onDown);
    this.control.addEventListener('pointermove', this.onMove);
    this.control.addEventListener('pointerup', this.onUp);
    this.control.addEventListener('pointercancel', this.onUp);
    this.control.addEventListener('keydown', this.onKey);
  }

  set(value) {
    this.value = Math.min(100, Math.max(0, value));
    this.stage.style.setProperty('--pos', `${this.value}%`);
    this.control.setAttribute('aria-valuenow', String(Math.round(this.value)));
  }

  fromPointer(event) {
    const rect = this.control.getBoundingClientRect();
    this.set(((event.clientX - rect.left) / rect.width) * 100);
  }

  onDown = (event) => {
    this.dragging = true;
    try {
      this.control.setPointerCapture(event.pointerId);
    } catch (_) {
      /* ignore */
    }
    this.fromPointer(event);
  };

  onMove = (event) => {
    if (this.dragging) this.fromPointer(event);
  };

  onUp = () => {
    this.dragging = false;
  };

  onKey = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.set(this.value + (event.key === 'ArrowLeft' ? -5 : 5));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.set(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.set(100);
    }
  };
}
customElements.define('curae-before-after', CuraeBeforeAfter);

/* ---------- Reviews strip ---------- */
class CuraeReviewTrack extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    const row = this.firstElementChild;
    if (!row) return;
    // Duplicate the cards once so the strip can loop without a visible end.
    Array.from(row.children).forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));
      row.appendChild(clone);
    });

    const speed = Number(this.dataset.speed ?? 4) / 100; // px per ms (design: 0.04)
    let hover = false;
    let drag = false;
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let idle = 0;
    let pos = this.scrollLeft;
    const half = () => (row.scrollWidth + 20) / 2;
    const wrap = () => {
      const h = half();
      if (h > 0) {
        if (this.scrollLeft >= h) this.scrollLeft -= h;
        else if (this.scrollLeft <= 0) this.scrollLeft += h;
      }
      pos = this.scrollLeft;
    };

    this.addEventListener('mouseenter', () => { hover = true; });
    this.addEventListener('mouseleave', () => { hover = false; drag = false; this.style.cursor = 'grab'; });
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      drag = true;
      moved = false;
      startX = e.clientX;
      startLeft = this.scrollLeft;
      this.style.cursor = 'grabbing';
    });
    window.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      this.scrollLeft = startLeft - dx;
      wrap();
    });
    window.addEventListener('pointerup', () => {
      if (!drag) return;
      drag = false;
      this.style.cursor = 'grab';
      idle = performance.now();
    });
    this.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
    this.addEventListener('wheel', (e) => {
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!d) return;
      e.preventDefault();
      this.scrollLeft += d;
      wrap();
      idle = performance.now();
    }, { passive: false });
    this.addEventListener('scroll', () => {
      if (Math.abs(this.scrollLeft - pos) > 2) { idle = performance.now(); wrap(); }
    }, { passive: true });

    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let last = performance.now();
    const tick = (now) => {
      if (!this.isConnected) return;
      const dt = Math.min(64, now - last);
      last = now;
      if (speed > 0 && !reduce && !hover && !drag && now - idle > 1500) {
        pos += dt * speed;
        this.scrollLeft = pos;
        if (Math.abs(this.scrollLeft - pos) > 2) pos = this.scrollLeft;
        wrap();
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}
customElements.define('curae-review-track', CuraeReviewTrack);

/* ---------- 3D molecules (compound cards) ---------- */
const MOLECULE_COLORS = {
  C: ['#A07A64', '#5A3E30'],
  O: ['#F6B7A2', '#C4624A'],
  N: ['#B9B98A', '#6A6A40'],
  H: ['#FFFFFF', '#D9C8BC'],
  B: ['#E4D3C7', '#B79E8E'],
};
const MOLECULE_SIZES = { C: 11, O: 12, N: 12, H: 7, B: 4 };

function buildMolecules() {
  const ring = (cx, cy, cz, R, rot, tilt) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = (rot || 0) + (i * Math.PI) / 3;
      return [cx + R * Math.cos(a), cy + R * Math.sin(a) * Math.cos(tilt || 0), cz + R * Math.sin(a) * Math.sin(tilt || 0)];
    });
  const cyc = (o) => [0, 1, 2, 3, 4, 5].map((i) => [o + i, o + ((i + 1) % 6)]);
  const out = (c, p, k, t) => [p[0] + (p[0] - c[0]) * k, p[1] + (p[1] - c[1]) * k, p[2] + (p[2] - c[2]) * k, t];
  const mk = (atoms, bonds) => ({ atoms, bonds });

  // polyphenol: one ring, three hydroxyls
  const r1 = ring(0, 0, 0, 15, 0, 0.5);
  const poly = mk(
    [...r1, out([0, 0, 0], r1[0], 0.75, 'O'), out([0, 0, 0], r1[2], 0.75, 'O'), out([0, 0, 0], r1[4], 0.75, 'O'), out([0, 0, 0], r1[1], 0.55, 'H'), out([0, 0, 0], r1[3], 0.55, 'H'), out([0, 0, 0], r1[5], 0.55, 'H')],
    [...cyc(0), [0, 6], [2, 7], [4, 8], [1, 9], [3, 10], [5, 11]]
  );
  // catechin: two rings + heterocycle oxygen
  const cA = ring(-18, 4, 0, 13, 0, 0.3);
  const cB = ring(18, -4, 8, 13, 0.3, -0.4);
  const cat = mk(
    [...cA, ...cB, [0, 0, 4, 'O'], out([-18, 4, 0], cA[3], 0.8, 'O'), out([18, -4, 8], cB[0], 0.8, 'O'), out([18, -4, 8], cB[5], 0.8, 'O')],
    [...cyc(0), ...cyc(6), [0, 12], [12, 9], [3, 13], [6, 14], [11, 15]]
  );
  // proanthocyanidin: stacked catechin pair
  const pA = ring(-16, -12, -6, 12, 0, 0.4);
  const pB = ring(14, -8, 8, 12, 0.4, -0.3);
  const pC = ring(-12, 16, 6, 12, 0.2, 0.6);
  const pD = ring(18, 18, -8, 12, 0, -0.5);
  const pac = mk(
    [...pA, ...pB, ...pC, ...pD, out([-16, -12, -6], pA[3], 0.8, 'O'), out([14, -8, 8], pB[0], 0.8, 'O'), out([-12, 16, 6], pC[3], 0.8, 'O'), out([18, 18, -8], pD[0], 0.8, 'O')],
    [...cyc(0), ...cyc(6), ...cyc(12), ...cyc(18), [0, 9], [1, 14], [13, 20], [3, 24], [6, 25], [15, 26], [18, 27]]
  );
  // taspine: three fused rings with nitrogen chain
  const t1 = ring(-22, 0, 0, 12, Math.PI / 6, 0.2);
  const t2 = ring(0, 0, 0, 12, Math.PI / 6, 0.2);
  const t3 = ring(11, 19, 4, 12, Math.PI / 6, 0.2);
  const tas = mk(
    [...t1, ...t2, ...t3, [24, -16, 6], [34, -26, 4, 'N'], [44, -20, 10], out([-22, 0, 0], t1[3], 0.8, 'O'), out([0, 0, 0], t2[4], 0.8, 'O')],
    [...cyc(0), ...cyc(6), ...cyc(12), [5, 9], [0, 8], [7, 13], [6, 12], [11, 18], [18, 19], [19, 20], [3, 21], [10, 22]]
  );
  // dimethylcedrusin: two rings bridged by a chain
  const l1 = ring(-24, -6, 0, 12, 0, 0.4);
  const l2 = ring(24, 6, 0, 12, 0, -0.4);
  const lig = mk(
    [...l1, ...l2, [-8, -2, 8], [0, 4, 10], [8, 2, 4], out([-24, -6, 0], l1[3], 0.8, 'O'), out([24, 6, 0], l2[0], 0.8, 'O'), [0, 16, 14, 'O']],
    [...cyc(0), ...cyc(6), [0, 12], [12, 13], [13, 14], [14, 9], [3, 15], [6, 16], [13, 17]]
  );
  // diterpene: three fused puckered rings
  const d1 = ring(-20, 0, 0, 12, Math.PI / 6, 0.9);
  const d2 = ring(0, 0, 0, 12, Math.PI / 6, -0.9);
  const d3 = ring(20, 0, 0, 12, Math.PI / 6, 0.9);
  const dit = mk(
    [...d1, ...d2, ...d3, [0, -24, 10], out([20, 0, 0], d3[0], 0.9, 'O'), [-26, 22, 6]],
    [...cyc(0), ...cyc(6), ...cyc(12), [0, 8], [5, 9], [6, 14], [11, 15], [10, 18], [12, 19], [2, 20]]
  );

  return {
    proanthocyanidins: { def: pac, duration: 18 },
    polyphenols: { def: poly, duration: 14 },
    taspine: { def: tas, duration: 20 },
    catechins: { def: cat, duration: 16 },
    dimethylcedrusin: { def: lig, duration: 17 },
    diterpenes: { def: dit, duration: 22 },
  };
}

let moleculeLibrary = null;

class CuraeMolecule extends HTMLElement {
  connectedCallback() {
    if (this.built) return;
    this.built = true;
    moleculeLibrary = moleculeLibrary || buildMolecules();
    const entry = moleculeLibrary[this.getAttribute('kind')] || moleculeLibrary.polyphenols;
    this.style.setProperty('--spin', `${entry.duration}s`);
    this.setAttribute('aria-hidden', 'true');

    const pts = entry.def.atoms.map((p) => ({ x: p[0], y: p[1], z: p[2], t: p[3] || 'C' }));
    const dots = [];
    entry.def.bonds.forEach(([p, q]) => {
      const A = pts[p];
      const B = pts[q];
      [0.2, 0.4, 0.6, 0.8].forEach((t) =>
        dots.push({ x: A.x + (B.x - A.x) * t, y: A.y + (B.y - A.y) * t, z: A.z + (B.z - A.z) * t, t: 'B' })
      );
    });

    const body = document.createElement('div');
    body.className = 'curae-molecule__body';
    dots.concat(pts).forEach((p) => {
      const r = MOLECULE_SIZES[p.t];
      const c = MOLECULE_COLORS[p.t];
      const node = document.createElement('div');
      node.className = 'curae-molecule__node';
      node.style.transform = `translate3d(${p.x}px,${p.y}px,${p.z}px)`;
      const atom = document.createElement('div');
      atom.className = 'curae-molecule__atom';
      atom.style.width = `${r}px`;
      atom.style.height = `${r}px`;
      atom.style.marginLeft = `${-r / 2}px`;
      atom.style.marginTop = `${-r / 2}px`;
      atom.style.background = `radial-gradient(circle at 32% 30%,${c[0]} 0%,${c[1]} 78%)`;
      atom.style.boxShadow = p.t === 'B' ? 'none' : '0 1px 2px rgba(58,46,40,.25)';
      node.appendChild(atom);
      body.appendChild(node);
    });
    this.appendChild(body);

    // Still until hovered; a tap starts it on touch screens.
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') this.toggleAttribute('data-playing', true);
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') this.toggleAttribute('data-playing', false);
    });
    this.addEventListener('click', () => {
      if (!matchMedia('(hover: hover)').matches) this.toggleAttribute('data-playing');
    });
  }
}
customElements.define('curae-molecule', CuraeMolecule);

/* ---------- UGC videos ---------- */
document.addEventListener('click', (event) => {
  const hit = event.target.closest('.curae-ugc__hit');
  if (!hit) return;
  const frame = hit.closest('.curae-ugc__frame');
  const video = frame && frame.querySelector('video');
  if (!video) return;
  event.preventDefault();
  document.querySelectorAll('.curae-ugc__frame[data-playing]').forEach((other) => {
    other.removeAttribute('data-playing');
    other.querySelector('video')?.pause();
  });
  frame.setAttribute('data-playing', '');
  video.controls = true;
  video.play().catch(() => {});
  video.focus({ preventScroll: true });
});

/* ---------- 10% off popup ---------- */
class CuraeOffer extends HTMLElement {
  static storageKey = 'curae-offer-dismissed';

  connectedCallback() {
    this.tab = this.querySelector('.curae-offer__tab');
    this.card = this.querySelector('.curae-offer__card');
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(CuraeOffer.storageKey) === '1';
    } catch (_) {
      /* ignore */
    }
    if (this.hasAttribute('data-posted')) {
      this.show('card');
    } else if (dismissed && !window.Shopify?.designMode) {
      this.show('none');
    } else {
      this.show('tab');
    }
    this.addEventListener('click', this.onClick);
    this.closest('form')?.addEventListener('submit', this.onSubmit);
  }

  show(state) {
    if (this.tab) this.tab.hidden = state !== 'tab';
    if (this.card) this.card.hidden = state !== 'card';
  }

  dismiss() {
    this.show('none');
    try {
      window.sessionStorage.setItem(CuraeOffer.storageKey, '1');
    } catch (_) {
      /* ignore */
    }
  }

  onClick = (event) => {
    if (event.target.closest('[data-offer-open]')) {
      event.preventDefault();
      this.show('card');
      this.querySelector('input[type="email"]')?.focus({ preventScroll: true });
    } else if (event.target.closest('[data-offer-dismiss]')) {
      event.preventDefault();
      this.dismiss();
    } else if (event.target.closest('[data-offer-close]')) {
      event.preventDefault();
      // Closing the form goes back to the tab; closing the thank-you note dismisses the offer.
      if (this.querySelector('.curae-offer__state--done')) this.dismiss();
      else this.show('tab');
    }
  };

  onSubmit = (event) => {
    const input = this.querySelector('input[type="email"]');
    const error = this.querySelector('[data-offer-error]');
    const email = (input?.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      event.preventDefault();
      if (error) error.hidden = false;
      input?.setAttribute('aria-invalid', 'true');
      return;
    }
    if (error) error.hidden = true;
    const button = this.querySelector('button[type="submit"]');
    const label = button?.querySelector('span');
    if (label && button.dataset.sendingLabel) label.textContent = button.dataset.sendingLabel;
  };
}
customElements.define('curae-offer', CuraeOffer);
